import { css } from "@emotion/css";
import { ActionButton } from "@octopusdeploy/design-system-components";
import type { DeprecatedDropdownMenuOption } from "@octopusdeploy/design-system-octopus-components";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { GitHubAppConnectionSummary, GitHubRepository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { GitHubInstallationLogo } from "~/areas/library/components/GitConnections/GitHubInstallationLogo";
import { GitConnectionOwner } from "~/areas/projects/components/ProjectSettings/VersionControl/Connection/VersionControlSettingsConnectionDetails";
import { IconButtonWithTooltip } from "../IconButtonWithTooltip";
import { useSpaceAwareNavigation } from "../Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { SummaryNode } from "../form";
import { Summary } from "../form";
import SelectWithAddRefresh from "../form/SelectWithAddRefresh/SelectWithAddRefresh";
import type { GitHubConnectionRepositorySelectorState } from "./useGitHubConnectionRepositorySelector";
export interface GitHubConnectionRepositorySelectorProps {
    spaceId: string | undefined;
    state: GitHubConnectionRepositorySelectorState;
}
export interface GitHubConnectionDetails {
    connectionId: string | undefined;
    connection: GitHubAppConnectionSummary | undefined;
    repositoryUrl: string | undefined;
    repository: GitHubRepository | undefined;
}
export function GitHubConnectionRepositorySelector({ spaceId, state }: GitHubConnectionRepositorySelectorProps) {
    const spaceAwareNavigation = useSpaceAwareNavigation();
    const connectionOptions = SelectOptionsMap(state.connections, (value) => value.Id, (value) => value.Installation?.AccountLogin ?? value.Id, (value) => (value.Installation ? <GitHubInstallationLogo small installation={value.Installation}/> : undefined));
    const repositoryOptions = SelectOptionsMap(state.repositories?.Repositories ?? [], (value) => value.RepositoryId, (value) => value.RepositoryName);
    const styles = {
        buttonContainer: css({
            display: "flex",
            alignItems: "center",
            gap: space[8],
        }),
    };
    return (<>
            {!state.connections || state.connections.length > 0 ? (spaceId ? (<SelectWithAddRefresh label="GitHub account" items={connectionOptions} value={state.connection?.Id} onChange={state.setConnection} sortItems={false} autoFocus={true} disabled={!state.connections} placeholder={state.connections ? "Select a GitHub account" : "Loading..."} addUrl={links.gitHubConnectionsPage.generateUrl({ spaceId })} onRequestRefresh={state.refreshConnections} allowFilter={true} selectionRenderer={(value, menuItem) => <RenderedDeprecatedDropdownMenuOption item={menuItem}/>}/>) : (<DeprecatedSelect label="GitHub account" items={connectionOptions} value={state.connection?.Id} onChange={state.setConnection} sortItems={false} autoFocus={true} disabled={!state.connections} placeholder={state.connections ? "Select a GitHub account" : "Loading..."} allowFilter={true} selectionRenderer={(value, menuItem) => <RenderedDeprecatedDropdownMenuOption item={menuItem}/>}/>)) : (<div className={styles.buttonContainer}>
                    {spaceId ? <ActionButton label="Connect to GitHub" accessibleName="Connect to GitHub" onClick={() => spaceAwareNavigation.open(links.gitHubConnectionsPage.generateUrl({ spaceId }))}/> : "No GitHub Connections"}
                    <IconButtonWithTooltip onClick={state.refreshConnections} toolTipContent={"Refresh"} icon={"ArrowRefreshIcon"} accessibleName="Refresh"/>
                </div>)}
            {state.connection &&
            (spaceId ? (<SelectWithAddRefresh label="GitHub repository" items={repositoryOptions} value={state.repository?.RepositoryId} onChange={state.setRepository} sortItems={false} disabled={!state.repositories} placeholder={state.repositories ? "Select a GitHub repository" : "Loading..."} addUrl={links.gitHubConnectionPage.generateUrl({ spaceId, connectionId: state.connection.Id })} onRequestRefresh={async () => state.refreshRepositories()} allowFilter={true}/>) : (<DeprecatedSelect label="GitHub repository" items={repositoryOptions} value={state.repository?.RepositoryId} onChange={state.setRepository} sortItems={false} disabled={!state.repositories} placeholder={state.repositories ? "Select a GitHub repository" : "Loading..."} allowFilter={true}/>))}
        </>);
}
const projectPlaceholderText = "Select a GitHub repository to store the configuration for this project";
const platformHubPlaceholderText = "Select a GitHub repository to connect to the platform hub";
const genericPlaceholderText = "Select a GitHub repository";
export function GitHubConnectionRepositoryHelpText(gitConnectionOwner: GitConnectionOwner) {
    if (gitConnectionOwner === GitConnectionOwner.PlatformHub) {
        return platformHubPlaceholderText;
    }
    else if (gitConnectionOwner === GitConnectionOwner.Project) {
        return projectPlaceholderText;
    }
    else {
        return genericPlaceholderText;
    }
}
export function GitHubConnectionRepositorySummary(connectionId: string | undefined, url: string | undefined, gitConnectionOwner: GitConnectionOwner, state: GitHubConnectionRepositorySelectorState): SummaryNode {
    if (!connectionId || !url) {
        // If they haven't finished selecting an account or repository, show a placeholder prompting them to do so.
        return Summary.placeholder(GitHubConnectionRepositoryHelpText(gitConnectionOwner));
    }
    if (state.connection && state.repository) {
        if (state.connection.Installation) {
            const connectionText = state.connection.Installation.AccountLogin;
            const repositoryText = state.repository.RepositoryName;
            const styles = {
                container: css({
                    display: "flex",
                    flexDirection: "row",
                    gap: space[8],
                    alignItems: "center",
                }),
            };
            return Summary.summary(<span className={styles.container}>
                    <GitHubInstallationLogo small installation={state.connection.Installation}/> {connectionText}/{repositoryText}
                </span>);
        }
        else {
            // We have a connection, but it's missing the installation details. This is rare, but possible if we
            // have a connection that's to an installation that's been removed, and don't have the details cached.
            return Summary.summary(url);
        }
    }
    // Even if they have a connectionId and repository URL selected, it's possible that we can't load it anymore. This
    // could be because we're still loading the details or the repository has been removed from the installation.
    if (state.repositories !== undefined) {
        // If we've loaded the repositories but we can't find the one we're looking for, just show the URL. This could
        // be because it's been removed from the connection or renamed. Either way, this should be enough for the user
        // to troubleshoot what's going on.
        return Summary.summary(url);
    }
    else {
        return Summary.placeholder("Loading...");
    }
}
function SelectOptionsMap<T>(items: T[] | undefined, valueSelector: (item: T) => string, textSelector: (item: T) => string, iconSelector: ((item: T) => JSX.Element | undefined) | undefined = undefined): DeprecatedDropdownMenuOption[] {
    if (!items) {
        return [];
    }
    return items.map((value) => {
        return {
            value: valueSelector(value),
            text: textSelector(value),
            icon: iconSelector ? iconSelector(value) : undefined,
        };
    });
}
interface RenderedDeprecatedDropdownMenuOptionProps {
    item: DeprecatedDropdownMenuOption;
}
export function RenderedDeprecatedDropdownMenuOption({ item }: RenderedDeprecatedDropdownMenuOptionProps) {
    const styles = {
        container: css({
            display: "flex",
            flexDirection: "row",
            gap: space[8],
            alignItems: "center",
        }),
    };
    return (<div className={styles.container}>
            {item.icon} {item.text}
        </div>);
}
